import { useCallback, useMemo, useState } from 'react';
import type { TicketListItem } from '../../api/types';
import { TicketDetailPanel } from '../../components/detail/TicketDetailPanel';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorNotice, Notice, StaleBanner } from '../../components/ui/Notice';
import { SkeletonBar } from '../../components/ui/Skeleton';
import { useResource } from '../../hooks/useResource';
import { useProjects } from '../../projects/ProjectsProvider';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import { ColumnHeader } from './BoardColumnView';
import { LaneSwitcher } from './LaneSwitcher';
import { LaneTargetChooser } from './LaneTargetChooser';
import { RefusalNotice } from './RefusalNotice';
import { SwimlaneView } from './SwimlaneView';
import { boardLayout, MIN_COLUMN_WIDTH } from './board-layout';
import {
  UNPLACED_COLUMN_KEY,
  buildColumns,
  columnsInLane,
  lanesOf,
  type BoardColumn,
} from './lane-columns';
import { droppableColumnKeys } from './drag-rules';
import { fetchProjectTickets, TICKET_WINDOW_MAX } from './project-tickets';
import { buildSwimlanes } from './swimlanes';
import { useBoardParams } from './useBoardParams';
import { useCardDrag } from './useCardDrag';
import { useViewportWidth } from './useViewportWidth';

/**
 * THE BOARD.
 *
 * 🔴 Six columns by default, one per LANE, because there are 26 statuses and a 26-column board is not
 * a board (`lane-columns.ts`). A lane expands to its statuses on demand.
 * 🔴 Swimlanes grouped by parent story, as in Or's Jira (shot 6) — `swimlanes.ts`.
 * 🔴 Dragging a card IS a transition, gated by `GET /tickets/:id/transitions` — `drag-rules.ts`.
 * 🔴 On a phone, ONE LANE AT A TIME with a swipe — `board-layout.ts`, `LaneSwitcher.tsx`.
 *
 * ⚠️ This component renders NO app chrome. The shell owns `App.tsx` and `components/layout/`; a
 * `TopBar` drawn here would be a second one.
 * ⚠️ The route must be inside `VocabularyProvider` — lanes, statuses and colours all come from it.
 */
export function BoardPage() {
  const vocab = useVocabulary();
  const { projectId, selectedId, lane, expandedLanes, toggleLane, setLane, select } =
    useBoardParams();
  const [collapsedStories, setCollapsedStories] = useState<ReadonlySet<string>>(new Set());

  const layout = boardLayout(useViewportWidth());

  /**
   * ⭐ The SHELL's list, not a second copy. `ProjectsProvider` fetches `/projects` once for the whole
   * app precisely because two copies of one list are two chances to disagree — and the shell's rail
   * and top-bar switcher already write the `?project=` this page reads.
   * ⛔ So there is no project picker in this header: one control for one piece of state.
   */
  const projects = useProjects();

  const tickets = useResource(
    (signal) => fetchProjectTickets(projectId ?? '', signal),
    [projectId],
    { enabled: Boolean(projectId) },
  );

  const ticketWindow = tickets.data;
  const items = useMemo(() => ticketWindow?.items ?? [], [ticketWindow]);
  const complete = ticketWindow?.complete ?? true;

  const onChanged = useCallback(() => tickets.reload(), [tickets]);

  /**
   * 🔴 THE ACTIVE STATUSES — the only list that still narrows a drop since PH-19.
   *
   * ⛔ It is NOT a stand-in for the old transition gate. `status_def.is_active = false` means "not
   * offered as somewhere new to go" (db/004), and the API enforces it independently:
   * `assertTargetIsUsable` answers 409 `'x' is a retired status and cannot be moved into`. A lane
   * column CAN contain a retired status — `buildColumns` keeps one on the board while a ticket is
   * still sitting in it — so without this the board would offer a drop that is certain to 409.
   * ⚠️ Empty until the vocabulary loads, which is correct: no drop is offered while we cannot say
   * whether a status is retired.
   */
  const activeStatuses = useMemo(
    () => new Set(vocab.statuses.filter((status) => status.is_active).map((status) => status.code)),
    [vocab.statuses],
  );

  const drag = useCardDrag(onChanged, activeStatuses);

  const occupiedStatuses = useMemo(
    () => new Set(items.map((ticket) => ticket.status)),
    [items],
  );

  const lanes = vocab.resource.data?.lanes ?? [];
  const columns = useMemo(
    () =>
      buildColumns({
        statuses: vocab.statuses,
        lanes,
        expandedLanes,
        occupiedStatuses,
      }),
    [vocab.statuses, lanes, expandedLanes, occupiedStatuses],
  );

  const { swimlanes, detachedCount } = useMemo(() => buildSwimlanes(items), [items]);

  const laneTabs = useMemo(() => lanesOf(columns), [columns]);
  const activeLane = lane && laneTabs.some((entry) => entry.code === lane) ? lane : laneTabs[0]?.code ?? '';
  const activeIndex = Math.max(0, laneTabs.findIndex((entry) => entry.code === activeLane));

  const visibleColumns = layout.laneAtATime ? columnsInLane(columns, activeLane) : columns;

  const counts = useMemo(() => countPerColumn(items, columns), [items, columns]);

  const droppable = useMemo(() => {
    if (!drag.ticket) return null;
    return droppableColumnKeys({
      columns: visibleColumns,
      ticketStatus: drag.ticket.status,
      transitions: drag.transitions,
      activeStatuses,
      refusedStatuses: drag.refusedStatuses,
    });
  }, [drag.ticket, drag.transitions, drag.refusedStatuses, activeStatuses, visibleColumns]);

  const refusalFor = useCallback(
    (column: BoardColumn) => {
      const decision = drag.decide(column);
      return decision && decision.kind === 'refused' ? decision.reason : null;
    },
    [drag],
  );

  const toggleStory = useCallback((key: string) => {
    setCollapsedStories((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const gridTemplate = `repeat(${Math.max(1, visibleColumns.length)}, minmax(${MIN_COLUMN_WIDTH}px, 1fr))`;
  const boardMinWidth = visibleColumns.length * MIN_COLUMN_WIDTH;

  const board = (
    <div className="min-w-0 overflow-x-auto">
      <div style={{ minWidth: boardMinWidth }}>
        <div
          className="sticky top-0 z-10 grid gap-2 border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {visibleColumns.map((column) => (
            <ColumnHeader
              key={column.key}
              column={column}
              count={counts.get(column.key) ?? 0}
              countIsFloor={!complete}
              expanded={expandedLanes.has(column.lane)}
              onToggleLane={toggleLane}
            />
          ))}
        </div>

        {swimlanes.map((swimlane) => (
          <SwimlaneView
            key={swimlane.key}
            swimlane={swimlane}
            columns={visibleColumns}
            gridTemplate={gridTemplate}
            collapsed={collapsedStories.has(swimlane.key)}
            compact={layout.laneAtATime}
            dragTicketId={drag.ticket?.id ?? null}
            droppableKeys={droppable}
            refusalFor={refusalFor}
            onToggleCollapsed={() => toggleStory(swimlane.key)}
            onDropOn={drag.drop}
            onDragStart={drag.begin}
            onDragEnd={drag.end}
            onOpen={(ticket) => select(ticket.id)}
            onChanged={onChanged}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-[color:var(--color-surface)]">
      <header className="flex flex-wrap items-center gap-3 border-b border-[color:var(--color-line)] px-3 py-2 sm:px-4">
        <h1 className="text-base font-semibold">Board</h1>
        <span className="flex-1" />
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          {layout.laneAtATime
            ? 'One lane at a time — swipe or use the tabs'
            : `${columns.length} columns · grouped by parent story`}
        </p>
      </header>

      <div className="space-y-2 px-3 pt-2 empty:hidden sm:px-4">
        {projects.error ? <ErrorNotice error={projects.error} onRetry={projects.reload} /> : null}

        {vocab.resource.error ? (
          <Notice tone="warning" title="Lanes, statuses and colours are unavailable">
            <p>
              {vocab.resource.error.reason ?? vocab.resource.error.message} — without the vocabulary
              the board cannot draw its columns, because a lane is a row in <code>lane_def</code> and
              this app never carries a copy of one.
            </p>
          </Notice>
        ) : null}

        {vocab.resource.data && lanes.length === 0 && vocab.statuses.length > 0 ? (
          <Notice tone="info" title="Lane names are unavailable — columns are labelled by code">
            <p>
              <code>GET /settings/lanes</code> returned nothing. The columns and their order are
              recovered from the statuses; the labels are not, and are shown as raw codes rather than
              invented.
            </p>
          </Notice>
        ) : null}

        {tickets.isStale && tickets.error ? (
          <StaleBanner error={tickets.error} loadedAt={tickets.loadedAt} onRetry={tickets.reload} />
        ) : null}

        {drag.failure ? (
          <RefusalNotice
            failure={drag.failure}
            busy={drag.busy}
            onChoose={(toStatus) => {
              if (drag.failure) void drag.chooseTarget(drag.failure.ticket, toStatus);
            }}
            onDismiss={drag.dismissFailure}
          />
        ) : null}

        {/* PH-29 — WHAT THE GITLAB HOOK DID, on a move that SUCCEEDED.
            ⛔ `tone` is never "error". The transition committed; `gitlab-link.ts` cannot throw exactly
               so that GitLab being unreachable does not undo a decision a human made. Colouring this
               red would tell the reader their move failed, which is false.
            ⭐ The failure branch shows the API's OWN sentence — e.g. "the GitLab write path is not
               configured on this deployment" — because that sentence is the only part that says what
               to do next. */}
        {drag.gitlabNotice ? (
          <Notice
            tone={drag.gitlabNotice.ok ? 'info' : 'warning'}
            title={
              drag.gitlabNotice.ok
                ? drag.gitlabNotice.created
                  ? 'GitLab issue created'
                  : 'Linked to its existing GitLab issue'
                : 'The move was saved — its GitLab issue was NOT created'
            }
          >
            {drag.gitlabNotice.ok ? (
              <p>
                {/* ⚠️ "created" vs "linked" is a real distinction: re-entering the status finds the
                    existing issue, and saying "created" would send someone looking for a second one. */}
                Issue <code>#{drag.gitlabNotice.issueIid}</code>{' '}
                {drag.gitlabNotice.created ? 'was created for' : 'is already linked to'} this ticket.
              </p>
            ) : (
              <>
                <p className="break-words">{drag.gitlabNotice.reason}</p>
                <p>
                  The status change itself was saved — this notice is about GitLab only. ⛔ Until this
                  is fixed the ticket has NO issue, so nothing here should be read as one existing.
                </p>
              </>
            )}
            <button
              type="button"
              onClick={drag.dismissGitlabNotice}
              className="h-11 rounded border border-[color:var(--color-line)] px-3 text-sm sm:h-9"
            >
              Dismiss
            </button>
          </Notice>
        ) : null}

        {!complete && ticketWindow ? (
          <Notice
            tone="warning"
            title={`Showing the first ${ticketWindow.items.length} of ${ticketWindow.total} tickets`}
          >
            <p>
              A board is several requests over a paginated list (the API caps a page at 100), and this
              one stops at {TICKET_WINDOW_MAX}. Every count above is therefore a FLOOR, written{' '}
              <code>12+</code>. ⛔ It is not recomputed to look exact.
            </p>
          </Notice>
        ) : null}

        {detachedCount > 0 ? (
          <Notice
            tone="info"
            title={`${detachedCount} sub-task${detachedCount === 1 ? '' : 's'} ${
              detachedCount === 1 ? 'has its parent' : 'have their parents'
            } outside the loaded window`}
          >
            <p>
              Drawn under <strong>No parent story</strong> and badged <span aria-hidden="true">↳</span>{' '}
              rather than hidden — a card that vanishes reads as a deleted ticket.
            </p>
          </Notice>
        ) : null}

        {columns.some((column) => column.key === UNPLACED_COLUMN_KEY) ? (
          <Notice tone="warning" title="Some tickets are in a status this board cannot place">
            <p>
              They are in the <strong>Not in any lane</strong> column. Either the status is newer than
              this page's vocabulary, or its lane no longer exists — <code>WAIT API</code> on Or's own
              board is exactly such a status.
            </p>
          </Notice>
        ) : null}
      </div>

      <div className="min-h-0 flex-1">
        {!projectId ? (
          <EmptyState
            title="Choose a project"
            detail="A board is per project (SRS FR-2), and 'all projects' would mix six teams' swimlanes into one screen. Pick one in the project rail — this is not an empty board."
          />
        ) : null}

        {projectId && tickets.isLoading ? <BoardSkeleton /> : null}

        {projectId && tickets.error && !tickets.isStale ? (
          <div className="p-3 sm:p-4">
            <ErrorNotice error={tickets.error} onRetry={tickets.reload} />
          </div>
        ) : null}

        {projectId && ticketWindow && items.length === 0 && !tickets.error ? (
          <EmptyState
            title="This project has no tickets yet"
            detail="The request succeeded and returned nothing — an empty project, not a failure."
          />
        ) : null}

        {projectId && ticketWindow && items.length > 0 ? (
          layout.laneAtATime ? (
            <LaneSwitcher lanes={laneTabs} activeIndex={activeIndex} onChange={setLane}>
              {board}
            </LaneSwitcher>
          ) : (
            <div className="h-full overflow-y-auto">{board}</div>
          )
        ) : null}
      </div>

      {drag.choice ? (
        <LaneTargetChooser
          choice={drag.choice}
          busy={drag.busy}
          onChoose={(toStatus) => {
            if (drag.choice) void drag.chooseTarget(drag.choice.ticket, toStatus);
          }}
          onCancel={drag.cancelChoice}
        />
      ) : null}

      {selectedId ? (
        <div className="fixed inset-0 z-40 lg:absolute lg:inset-y-0 lg:right-0 lg:left-auto lg:w-[30rem]">
          <TicketDetailPanel
            /* PH-62 — the GitLab section needs the project's link. ⭐ Read off the ALREADY-LOADED
               project list rather than fetching per ticket: one nullable field is not worth a
               client-side N+1. Absent ⇒ null ⇒ the section is absent, which is correct for an
               unlinked project. */
            projectGitlabId={
              projects.data?.find((p) => p.id === projectId)?.gitlab_project_id ?? null
            }
            ticketId={selectedId}
            onClose={() => select(null)}
            onOpenTicket={(id) => select(id)}
            onStatusChanged={onChanged}
          />
        </div>
      ) : null}
    </div>
  );
}

function countPerColumn(
  tickets: readonly TicketListItem[],
  columns: readonly BoardColumn[],
): Map<string, number> {
  const ofStatus = new Map<string, string>();
  for (const column of columns) {
    for (const status of column.statuses) ofStatus.set(status, column.key);
  }

  const counts = new Map<string, number>();
  for (const ticket of tickets) {
    const key = ofStatus.get(ticket.status);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

function BoardSkeleton() {
  return (
    <div role="status" aria-label="Loading the board" className="grid grid-cols-2 gap-2 p-3 md:grid-cols-6">
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="space-y-2">
          <SkeletonBar className="h-4 w-24" />
          <SkeletonBar className="h-20 w-full" />
          <SkeletonBar className="h-20 w-full" />
        </div>
      ))}
    </div>
  );
}

export default BoardPage;
